import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface DecimalFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  containerClassName?: string;
  labelClassName?: string;
}

export function DecimalField({
  id,
  label,
  helpText,
  errorText,
  className,
  containerClassName,
  labelClassName,
  inputMode = 'decimal',
  ...props
}: DecimalFieldProps): React.ReactElement {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helpId = helpText ? `${fieldId}-help` : undefined;
  const errorId = errorText ? `${fieldId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={classNames('min-w-0 space-y-1.5', containerClassName)}>
      <label
        className={classNames(
          'block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted',
          labelClassName,
        )}
        htmlFor={fieldId}
      >
        {label}
      </label>
      <input
        {...props}
        id={fieldId}
        type="text"
        inputMode={inputMode}
        aria-describedby={describedBy}
        aria-invalid={errorText ? true : undefined}
        className={classNames(inputClassName, className)}
      />
      {helpText ? (
        <p id={helpId} className="text-xs leading-5 text-ink-muted">
          {helpText}
        </p>
      ) : null}
      {errorText ? (
        <p id={errorId} role="alert" className="text-xs leading-5 text-clay">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

const inputClassName =
  'block min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] outline-none transition placeholder:text-ink-muted focus:border-ochre focus:ring-2 focus:ring-ochre/30 disabled:cursor-not-allowed disabled:bg-paper-deep disabled:text-ink-muted';

function classNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(' ');
}
